/**
 * CMS preview, request by request.
 *
 * Drafts are readable only with a Sanity token, so holding one is the whole of
 * the switch: where `SANITY_READ_TOKEN` is set, `/preview/enable` opens to the
 * Studio's Preview tool, which arrives with a one-time secret minted in the
 * dataset, leaves with a cookie, and every request carrying that cookie is a
 * preview request (`src/routing/cmsPreview.ts`): served from the drafts, held
 * in no cache, framed by the Studio, `no-store` and `noindex`. Every other
 * request is served as it always was, the prerendered published page.
 * Production keeps serving its prerendered pages and carries the preview
 * beside it: no staging host, and no second flag that could disagree with the
 * token.
 */

/**
 * Whether this site can read drafts at all, which is what opens the door. A
 * site without the token cannot serve a draft however it is asked, so there is
 * nothing else to switch off.
 */
export function previewDoorOpen(): boolean {
  return Boolean(process.env.SANITY_READ_TOKEN);
}

/**
 * The content layer asks; the server entry answers, per request. The answer
 * is installed by `src/routing/cmsPreview.ts`, which holds the request scope
 * and is server-only. This module stays free of it so that code the browser
 * also gets may import it.
 */
let readsDrafts: () => boolean = () => false;

export function answerPreviewRequests(reader: () => boolean): void {
  readsDrafts = reader;
}

/** Whether the request being served reads drafts. */
export function isCmsPreview(): boolean {
  return readsDrafts();
}

/**
 * The Studio that frames a preview, which the page's content security policy
 * admits. Overridden for a Studio run locally.
 */
export function previewStudioOrigin(): string {
  return (
    process.env.CMS_PREVIEW_STUDIO_ORIGIN ?? "https://webshare.sanity.studio"
  );
}

/**
 * Every origin allowed to frame a preview. A hosted Studio opens inside
 * Sanity's Dashboard at www.sanity.io, so the page has two ancestors, the
 * Studio and the Dashboard around it, and a browser checks each of them.
 * `https://*.sanity.io` is the value Sanity's Dashboard docs give.
 */
export function previewFrameAncestors(): string {
  return `'self' ${previewStudioOrigin()} https://*.sanity.io`;
}
